// Comments & votes on a Showcase project page (12 Showcase V3; snippets/ns-showcase-comments.liquid; the owner's decision,
// 2026-10-18: comments are approved before they show). The block stays hidden until the campaign Worker answers the list
// (/apps/9space/showcase/comments) with a 200: with SHOWCASE_COMMENTS_READY off it answers not_ready, so neither the list nor
// the form is offered. The list holds approved comments only, as the Worker sends them, newest first; a comment is written
// with textContent, never as HTML. The form posts through the app proxy and says the comment shows after a quick review.
// Standalone on purpose: it imports nothing, so the import map in layout/theme.liquid needs no entry.
export const TAGS = ['great_idea', 'needs_work', 'buy_it_now', 'another_color', 'too_pricey'];
export const MAX_TEXT = 800;
export const MAX_NAME = 40;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SOURCE = /^[a-z0-9_-]{1,40}$/;
const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
// The Worker's link rule (services/campaigns/showcase-comments.js), so the shopper hears about a link before posting. An email
// address is not a link here: the Worker turns it into [email].
const TLDS = 'com|net|org|io|ly|gg|xyz|shop|store|ru|cn|vn|th|info|biz|site|online|top|club|link|app|dev|click|icu|buzz|tk|ws|su|cc|uk|au|jp|kr|br|ph|sg|hk|tw|eu|nl|ua|de|fr';
const SCHEME = /[a-z][a-z0-9+.-]*:\/\/|\bwww\.\S/i;
const DOMAIN = new RegExp(`\\b[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?(?:\\.[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)*\\.(?:${TLDS}|${TLDS.toUpperCase()})\\b`);
const EMAILS = /[^\s<>()@]+@[^\s<>()@]+/g;
export const hasLink = value => { const text = String(value ?? '').replace(EMAILS, ' '); return SCHEME.test(text) || DOMAIN.test(text); };

export const listUrl = (endpoint, project) => `${endpoint}?project=${encodeURIComponent(project)}`;
// The request key also travels in the query: the app proxy forwards the query, signed, whatever it does with headers.
export const keyedUrl = (endpoint, key) => `${endpoint}${endpoint.includes('?') ? '&' : '?'}key=${encodeURIComponent(key)}`;
// An ad's utm_source names the entry point; otherwise the section's own source.
export function sourceFrom(search, fallback) {
  const utm = String(new URLSearchParams(search ?? '').get('utm_source') ?? '').trim().toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 40);
  return utm || (SOURCE.test(fallback ?? '') ? fallback : 'showcase');
}

// The checks the shopper can fix before posting; the Worker repeats them and scrubs contact details out of the text.
export function commentPayload(project, fields, { locale, source } = {}) {
  const text = String(fields.text ?? '').trim();
  if (text.length < 2 || text.length > MAX_TEXT) return { ok: false, error: 'text' };
  if (hasLink(text)) return { ok: false, error: 'links' };
  const name = String(fields.name ?? '').trim();
  if (name.length > MAX_NAME || /[\d@/:<>]/.test(name) || hasLink(name)) return { ok: false, error: 'name' };
  const email = String(fields.email ?? '').trim();
  if (!EMAIL.test(email) || email.length > 254) return { ok: false, error: 'email' };
  if (fields.consent !== true) return { ok: false, error: 'consent' };
  const body = { project: String(project), text, email, consent: true, locale: String(locale || 'en').slice(0, 12) };
  if (name) body.name = name;
  if (TAGS.includes(fields.tag)) body.tag = fields.tag;
  if (SOURCE.test(source ?? '')) body.source = source;
  return { ok: true, body };
}

// The line the shopper reads for the Worker's answer: done only on a 2xx that says saved.
export function resultView(status, json) {
  if (status >= 200 && status < 300 && json?.saved === true) return 'done';
  if (status === 503 && json?.error === 'not_ready') return 'unavailable';
  if (status === 429) return 'busy';
  const details = status === 422 && Array.isArray(json?.details) ? json.details.map(String) : [];
  if (details.some(detail => /link/.test(detail))) return 'links';
  for (const field of ['text', 'name', 'email', 'consent']) if (details.some(detail => detail.startsWith(`${field} `))) return field;
  return 'server';
}

// The store's day (YYYY-MM-DD) in the shopper's language; Thai reads it in Thai.
export function dateLabel(date, locale) {
  const match = DAY.exec(String(date ?? ''));
  if (!match) return '';
  try {
    return new Intl.DateTimeFormat(locale || 'en', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  } catch {
    return String(date);
  }
}

const camel = tag => tag.replace(/(?:^|_)([a-z])/g, (_, letter) => letter.toUpperCase());
// The labels the section rendered in the shopper's language, from the block's data attributes (data-tag-great-idea, …).
export const labelsFrom = (dataset, locale) => ({ anonymous: dataset.anonymous ?? '', locale, tags: Object.fromEntries(TAGS.map(tag => [tag, dataset[`tag${camel(tag)}`] ?? ''])) });

export function commentView(row, labels) {
  const name = typeof row?.name === 'string' && row.name.trim() ? row.name.trim() : labels.anonymous;
  const tag = TAGS.includes(row?.tag) ? row.tag : null;
  const day = DAY.test(String(row?.date ?? '')) ? row.date : '';
  return { name, initial: [...name][0]?.toLocaleUpperCase() ?? '', date: dateLabel(day, labels.locale), datetime: day, text: typeof row?.text === 'string' ? row.text : '', tag, tagLabel: tag ? labels.tags[tag] ?? '' : '' };
}

// Builds one row per comment that has text: the initial (decoration), the name, the date, the text and the vote chip.
export function renderComments(list, rows, labels, create) {
  const element = (tag, className, text) => { const node = create(tag); node.className = className; if (text !== undefined) node.textContent = text; return node; };
  const items = (Array.isArray(rows) ? rows : []).map(row => commentView(row, labels)).filter(view => view.text).map(view => {
    const item = element('li', 'ns-comment');
    const avatar = element('span', 'ns-comment__avatar', view.initial);
    avatar.setAttribute('aria-hidden', 'true');
    const body = element('div', 'ns-comment__body');
    const meta = element('p', 'ns-comment__meta');
    meta.append(element('strong', 'ns-comment__name', view.name));
    if (view.date) {
      const time = element('time', 'ns-comment__date', view.date);
      time.setAttribute('datetime', view.datetime);
      meta.append(time);
    }
    body.append(meta, element('p', 'ns-comment__text', view.text));
    if (view.tagLabel) body.append(element('span', `ns-comment__tag ns-comment__tag--${view.tag}`, view.tagLabel));
    item.append(avatar, body);
    return item;
  });
  list.replaceChildren(...items);
  return items.length;
}

// Reads the approved comments. Any answer but a 200 with a list keeps the whole block hidden. With none, an open project shows
// the empty line beside its form; a project without a form stays hidden.
export async function loadComments(root, { fetcher = fetch, create, locale } = {}) {
  const list = root.querySelector('[data-ns-comments-list]');
  const empty = root.querySelector('[data-ns-comments-empty]');
  const form = root.querySelector('[data-ns-comment-form]');
  let json = null;
  try {
    const response = await fetcher(listUrl(root.dataset.endpoint, root.dataset.project), { headers: { Accept: 'application/json' } });
    json = response.ok ? await response.json() : null;
  } catch {
    json = null;
  }
  if (!Array.isArray(json?.comments)) { root.hidden = true; return 'hidden'; }
  const shown = list ? renderComments(list, json.comments, labelsFrom(root.dataset, locale), create) : 0;
  if (list) list.hidden = shown === 0;
  if (!shown && !form) { root.hidden = true; return 'hidden'; }
  if (empty) empty.hidden = shown > 0;
  root.hidden = false;
  return shown ? 'list' : 'empty';
}

// The vote chips are toggle buttons: one pressed at most, and pressing it again clears the vote. Returns the vote.
export function pressTag(buttons, pressed) {
  const on = pressed.getAttribute('aria-pressed') !== 'true';
  for (const button of buttons) button.setAttribute('aria-pressed', String(on && button === pressed));
  return on ? pressed.dataset.nsCommentTag : '';
}

const FIELDS = { text: 'text', links: 'text', name: 'name', email: 'email', consent: 'consent' };

if (typeof document !== 'undefined') {
  const create = tag => document.createElement(tag);
  const locale = document.documentElement.lang || 'en';
  const keys = new Map();
  async function post(endpoint, body) {
    const json = JSON.stringify(body);
    if (!keys.has(json)) keys.set(json, crypto.randomUUID());
    const key = keys.get(json);
    try {
      const response = await fetch(keyedUrl(endpoint, key), { method: 'POST', headers: { 'Content-Type': 'application/json', 'Idempotency-Key': key }, body: json });
      return resultView(response.status, await response.json().catch(() => null));
    } catch {
      return 'server';
    }
  }
  function showError(form, key) {
    const error = form.querySelector('[data-ns-comment-error]');
    error.textContent = error.dataset[key] ?? error.dataset.server;
    error.hidden = false;
    const field = FIELDS[key] ? form.elements.namedItem(FIELDS[key]) : null;
    if (field) { field.setAttribute('aria-invalid', 'true'); field.focus(); }
  }

  document.addEventListener('click', event => {
    const button = event.target instanceof Element ? event.target.closest('[data-ns-comment-tag]') : null;
    const form = button?.closest('[data-ns-comment-form]');
    if (!form) return;
    form.elements.namedItem('tag').value = pressTag([...form.querySelectorAll('[data-ns-comment-tag]')], button);
  });

  document.addEventListener('submit', async event => {
    const form = event.target instanceof HTMLFormElement && event.target.matches('[data-ns-comment-form]') ? event.target : null;
    if (!form) return;
    event.preventDefault();
    if (form.dataset.pending === 'true') return;
    for (const field of form.querySelectorAll('[aria-invalid]')) field.removeAttribute('aria-invalid');
    form.querySelector('[data-ns-comment-error]').hidden = true;
    const value = name => form.elements.namedItem(name)?.value ?? '';
    const fields = { text: value('text'), name: value('name'), email: value('email'), tag: value('tag'), consent: form.elements.namedItem('consent')?.checked === true };
    const checked = commentPayload(form.dataset.project, fields, { locale, source: sourceFrom(window.location.search, form.dataset.source) });
    if (!checked.ok) { showError(form, checked.error); return; }
    const submit = form.querySelector('[type="submit"]');
    form.dataset.pending = 'true';
    submit.disabled = true;
    const view = await post(form.dataset.endpoint, checked.body);
    form.dataset.pending = 'false';
    submit.disabled = false;
    if (view !== 'done') { showError(form, view); return; }
    const done = form.parentElement.querySelector('[data-ns-comment-done]');
    form.hidden = true;
    if (done) { done.hidden = false; done.focus(); }
  });

  const start = () => { for (const root of document.querySelectorAll('[data-ns-comments]')) loadComments(root, { create, locale }); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
}
